/**
 * Browser Detection and Optimization Utilities
 * Detects browser type and applies specific optimizations
 */

export interface BrowserInfo {
  name: string
  version: string
  isChrome: boolean
  isSafari: boolean
  isFirefox: boolean
  isEdge: boolean
  isBrave: boolean
  needsOptimization: boolean
}

/**
 * Detect the current browser
 */
export const detectBrowser = (): BrowserInfo => {
  const ua = navigator.userAgent
  const vendor = navigator.vendor || ''
  
  // Detect Brave
  const isBrave = !!(navigator as any).brave && typeof (navigator as any).brave.isBrave === 'function'
  
  // Detect Chrome (including Brave)
  const isChrome = /Chrome/.test(ua) && /Google Inc/.test(vendor) && !isBrave
  
  // Detect Safari
  const isSafari = /Safari/.test(ua) && /Apple Computer/.test(vendor)
  
  // Detect Firefox
  const isFirefox = /Firefox/.test(ua)
  
  // Detect Edge
  const isEdge = /Edg/.test(ua)
  
  // Extract version
  let version = 'unknown'
  if (isChrome || isBrave) {
    const match = ua.match(/Chrome\/(\d+)/)
    version = match ? match[1] : 'unknown'
  } else if (isSafari) {
    const match = ua.match(/Version\/(\d+)/)
    version = match ? match[1] : 'unknown'
  } else if (isFirefox) {
    const match = ua.match(/Firefox\/(\d+)/)
    version = match ? match[1] : 'unknown'
  } else if (isEdge) {
    const match = ua.match(/Edg\/(\d+)/)
    version = match ? match[1] : 'unknown'
  }
  
  // Determine browser name
  let name = 'Unknown'
  if (isBrave) name = 'Brave'
  else if (isChrome) name = 'Chrome'
  else if (isSafari) name = 'Safari'
  else if (isFirefox) name = 'Firefox'
  else if (isEdge) name = 'Edge'
  
  // Safari works perfectly, others need optimization
  const needsOptimization = !isSafari
  
  return {
    name,
    version,
    isChrome,
    isSafari,
    isFirefox,
    isEdge,
    isBrave,
    needsOptimization
  }
}

/**
 * Apply browser-specific optimizations
 */
export const applyBrowserOptimizations = () => {
  const browser = detectBrowser()
  
  console.log(`🌐 Browser detected: ${browser.name} ${browser.version}`)
  
  if (!browser.needsOptimization) {
    console.log('✅ Browser is Safari - no additional optimizations needed')
    return
  }
  
  console.log('🔧 Applying cross-browser optimizations...')
  
  // Add browser-specific class to body
  document.body.classList.add(`browser-${browser.name.toLowerCase()}`)
  
  // Chrome/Brave specific optimizations
  if (browser.isChrome || browser.isBrave) {
    applyChromeOptimizations()
  }
  
  // Firefox specific optimizations
  if (browser.isFirefox) {
    applyFirefoxOptimizations()
  }
  
  // Edge specific optimizations
  if (browser.isEdge) {
    applyEdgeOptimizations()
  }
  
  console.log('✅ Browser optimizations applied')
}

/**
 * Chrome/Brave specific optimizations
 */
const applyChromeOptimizations = () => {
  const style = document.createElement('style')
  style.id = 'chrome-optimizations'
  style.textContent = `
    /* Chrome/Brave: Optimized compositing */
    .browser-chrome .scroll-optimized,
    .browser-brave .scroll-optimized {
      will-change: transform;
    }
    
    /* Chrome/Brave: Fix flickering during animations */
    .browser-chrome .page-cache-container,
    .browser-brave .page-cache-container {
      -webkit-transform: translate3d(0, 0, 0);
      transform: translate3d(0, 0, 0);
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
    }
    
    /* Chrome/Brave: Optimize scrolling */
    .browser-chrome body,
    .browser-brave body {
      -webkit-overflow-scrolling: touch;
      overflow-scrolling: touch;
    }
    
    /* Chrome/Brave: Reduce paint operations */
    .browser-chrome .page-cache-container > div,
    .browser-brave .page-cache-container > div {
      contain: layout style;
    }
  `
  document.head.appendChild(style)
}

/**
 * Firefox specific optimizations
 */
const applyFirefoxOptimizations = () => {
  const style = document.createElement('style')
  style.id = 'firefox-optimizations'
  style.textContent = `
    /* Firefox: Optimize font rendering */
    .browser-firefox * {
      -moz-osx-font-smoothing: grayscale;
    }
    
    /* Firefox: Fix backface visibility */
    .browser-firefox .page-cache-container,
    .browser-firefox .page-cache-container > div {
      -moz-backface-visibility: hidden;
      backface-visibility: hidden;
    }
    
    /* Firefox: Optimize transforms */
    .browser-firefox .page-cache-container {
      -moz-transform: translate3d(0, 0, 0);
      transform: translate3d(0, 0, 0);
    }
  `
  document.head.appendChild(style)
}

/**
 * Edge specific optimizations
 */
const applyEdgeOptimizations = () => {
  const style = document.createElement('style')
  style.id = 'edge-optimizations'
  style.textContent = `
    /* Edge: Optimize scrolling */
    .browser-edge * {
      -ms-overflow-style: -ms-autohiding-scrollbar;
    }
    
    /* Edge: Fix rendering issues */
    .browser-edge .page-cache-container {
      -webkit-transform: translate3d(0, 0, 0);
      transform: translate3d(0, 0, 0);
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
    }
  `
  document.head.appendChild(style)
}

/**
 * Check if browser supports a specific CSS feature
 */
export const supportsCSS = (property: string, value: string): boolean => {
  if (typeof CSS === 'undefined' || !CSS.supports) {
    return false
  }
  return CSS.supports(property, value)
}

/**
 * Get optimal animation settings for current browser
 */
export const getOptimalAnimationSettings = () => {
  const browser = detectBrowser()
  
  // Safari gets the full experience
  if (browser.isSafari) {
    return {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94],
      useGPU: true,
      reducedMotion: false
    }
  }
  
  // Chrome/Brave need slightly adjusted settings
  if (browser.isChrome || browser.isBrave) {
    return {
      duration: 0.35,
      ease: [0.25, 0.46, 0.45, 0.94],
      useGPU: true,
      reducedMotion: false
    }
  }
  
  // Firefox and Edge get conservative settings
  return {
    duration: 0.3,
    ease: 'easeInOut',
    useGPU: false,
    reducedMotion: false
  }
}

/**
 * Log browser info to console (for debugging)
 */
export const logBrowserInfo = () => {
  const browser = detectBrowser()
  const animSettings = getOptimalAnimationSettings()
  
  console.group('🌐 Browser Information')
  console.log('Name:', browser.name)
  console.log('Version:', browser.version)
  console.log('Needs Optimization:', browser.needsOptimization)
  console.log('Animation Settings:', animSettings)
  console.groupEnd()
}
